import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppShellAdmin, Button, useThemeMode } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { AtlasBrand } from "../../components/AtlasBrand";
import { podeAcessar, readAverbadoraPermissoesFromJwt } from "../../lib/averbadora-perms";

const NAV = [
  { key: "dashboard", label: "Dashboard", href: "/averbadora/dashboard", icon: "◉" },
  // Ordem pedida pelo cliente (16/07/2026): Dashboard > Prefeituras > Bancos > Servidores > Convênios.
  { key: "organizacoes", label: "Órgãos e entidades", href: "/averbadora/organizacoes", icon: "▤" },
  // Item próprio, ao lado do cadastro: a relação é entre DUAS organizações, e
  // procurá-la dentro da ficha de uma delas obriga a adivinhar por qual ponta
  // começar. A chave de permissão continua sendo `prefeituras` (via alias) —
  // trocá-la revogaria o acesso de todo perfil já gravado no instante do deploy.
  { key: "relacoes", label: "Relações institucionais", href: "/averbadora/relacoes", icon: "⇄" },
  { key: "bancos", label: "Consignatárias", href: "/averbadora/bancos", icon: "▦" },
  {
    key: "servidores",
    label: "Servidores",
    icon: "◎",
    children: [
      { key: "servidores-visualizar", label: "Ver servidores", href: "/averbadora/servidores/visualizar" },
      { key: "servidores-importar", label: "Importar servidores", href: "/averbadora/servidores/importar" },
    ],
  },
  { key: "convenios", label: "Convênios", href: "/averbadora/convenios", icon: "▣" },
  { key: "contratos", label: "Contratos", href: "/averbadora/contratos", icon: "▧" },
  { key: "folhas", label: "Folhas", href: "/averbadora/folhas", icon: "▥" },
  { key: "troca-arquivos", label: "Troca de arquivos", href: "/averbadora/troca-arquivos", icon: "⇄" },
  // O arquivo mensal da consignatária (#198). A API guarda com `prefeituras`
  // (alias em averbadora-perms.ts): um item que abre e responde 403 seria pior
  // do que não ter o item.
  { key: "consignatarias", label: "Arquivo da consignatária", href: "/averbadora/consignatarias/arquivos", icon: "⇪" },
  { key: "layouts", label: "Layouts", href: "/averbadora/layouts", icon: "▤" },
  { key: "pre-reservas", label: "Pré-reservas", href: "/averbadora/pre-reservas", icon: "⧖" },
  { key: "id-unico", label: "ID único", href: "/averbadora/id-unico", icon: "#" },
  { key: "rubricas", label: "Motor de rubricas", href: "/averbadora/rubricas", icon: "❋" },
  // Pedidos do servidor sobre planos, seguros e mensalidades (#276).
  { key: "solicitacoes-cancelamento", label: "Pedidos de cancelamento", href: "/averbadora/solicitacoes-cancelamento", icon: "✕" },
  { key: "trava-margem", label: "Trava de margem", href: "/averbadora/trava-margem", icon: "⊘" },
  { key: "bate-carteira", label: "Bate de carteira", href: "/averbadora/bate-carteira", icon: "⇄" },
  { key: "adf", label: "ADF", href: "/averbadora/adf", icon: "▤" },
  { key: "portabilidade", label: "Portabilidade", href: "/averbadora/portabilidade", icon: "⇌" },
  { key: "refinanciamentos", label: "Refinanciamentos", href: "/averbadora/refinanciamentos", icon: "↻" },
  { key: "auditoria", label: "Auditoria", href: "/averbadora/auditoria", icon: "⚖" },
  { key: "perfis", label: "Usuários", href: "/averbadora/perfis", icon: "⚙" },
  {
    key: "comunicados",
    label: "Comunicados",
    icon: "❐",
    children: [
      { key: "comunicados-banco", label: "Consignatária", href: "/averbadora/comunicados/banco" },
      { key: "comunicados-servidor", label: "Servidor", href: "/averbadora/comunicados/servidor" },
      { key: "comunicados-prefeitura", label: "Prefeitura", href: "/averbadora/comunicados/prefeitura" },
    ],
  },
  {
    key: "email-sistema",
    label: "E-mails do sistema",
    icon: "✉",
    children: [
      { key: "email-primeiro-acesso", label: "Primeiro acesso", href: "/averbadora/emails/primeiro-acesso" },
      { key: "email-recuperar-senha", label: "Recuperar senha", href: "/averbadora/emails/recuperar-senha" },
      { key: "email-redefinir-senha", label: "Redefinir senha", href: "/averbadora/emails/redefinir-senha" },
      { key: "email-simulacao", label: "Simulação", href: "/averbadora/emails/simulacao" },
      { key: "email-beneficios", label: "Benefícios", href: "/averbadora/emails/beneficios" },
      { key: "email-pedidos-cancelamento", label: "Pedidos de cancelamento", href: "/averbadora/emails/pedidos-cancelamento" },
      { key: "email-arquivo-pendente", label: "Arquivo mensal pendente", href: "/averbadora/emails/arquivo-pendente" },
    ],
  },
  { key: "termos", label: "Termos de aceite", href: "/averbadora/termos", icon: "📜" },
  { key: "health", label: "Health", href: "/averbadora/health", icon: "♥" },
  { key: "verify", label: "Verificação", href: "/averbadora/verify", icon: "✓" },
  { key: "logs", label: "Logs", href: "/averbadora/logs", icon: "≡" },
  { key: "vitrine", label: "Vitrine", href: "/averbadora/vitrine", icon: "▢" },
  { key: "beneficios", label: "Benefícios", href: "/averbadora/beneficios", icon: "◇" },
  { key: "telemedicina", label: "Telemedicina", href: "/averbadora/telemedicina", icon: "📱" },
  {
    key: "api",
    label: "API",
    icon: "⌘",
    children: [
      { key: "api-docs", label: "Documentação", href: "/averbadora/api/docs" },
      { key: "api-tokens", label: "Tokens de acesso", href: "/averbadora/api/tokens" },
      { key: "api-uso", label: "Uso de API", href: "/averbadora/api/uso" },
      { key: "api-webhooks", label: "Webhooks", href: "/averbadora/api/webhooks" },
    ],
  },
  { key: "configuracoes", label: "Configurações", href: "/averbadora/configuracoes", icon: "⚙" },
  { key: "suporte", label: "Suporte", href: "/averbadora/suporte", icon: "☎" },
  { key: "conta", label: "Minha conta", href: "/averbadora/conta", icon: "👤" },
];

export function AverbadoraLayout() {
  const nav = useNavigate();
  const location = useLocation();
  const { resolved, setMode } = useThemeMode();
  const parts = location.pathname.split("/").filter(Boolean);
  // Menus com subitens: api-*, comunicados-*, email-* (submenus dentro de
  // /averbadora/emails/*). Sem esse tratamento, activeKey vira "emails" —
  // chave inexistente em RESOURCE_GROUPS/PRESETS, redirecionando qualquer
  // nao-supervisor pra dashboard toda vez que abre um sub-item de email.
  const activeKey = (() => {
    if (parts[1] === "emails" && parts[2]) return `email-${parts[2]}`;
    if ((parts[1] === "api" || parts[1] === "comunicados" || parts[1] === "servidores") && parts[2])
      return `${parts[1]}-${parts[2]}`;
    return parts[1] ?? "dashboard";
  })();

  // Le as permissoes granulares do JWT (claim averbadora_permissoes). O
  // super-admin vem com ["*"]. Token sem a claim (sessao antiga) cai como
  // supervisor de fato — permite tudo.
  const [permissoes, setPermissoes] = useState<string[] | null>(null);
  // `null` tambem e' "sem claim = supervisor"; este flag separa "ainda nao li" de
  // "li e nao ha claim", para a contagem do menu nao disparar antes da leitura.
  const [permissoesLidas, setPermissoesLidas] = useState(false);
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("atlas:tokens");
      const token = stored ? JSON.parse(stored)?.access_token : null;
      setPermissoes(readAverbadoraPermissoesFromJwt(token));
    } catch {
      setPermissoes(null);
    }
    setPermissoesLidas(true);
  }, [location.pathname]); // reavalia ao trocar de tela (novo token via refresh)

  // Redireciona quem tenta abrir uma URL fora do escopo das permissoes.
  // Sub-itens (foo-bar) herdam o grant do parent `foo` se ele tiver.
  useEffect(() => {
    if (!permissoes || permissoes.includes("*")) return;
    const parentKey = activeKey.includes("-") ? activeKey.split("-")[0]! : activeKey;
    if (!podeAcessar(permissoes, activeKey) && !podeAcessar(permissoes, parentKey)) {
      nav("/averbadora/dashboard", { replace: true });
    }
  }, [permissoes, activeKey, nav]);

  // Cadastros do site institucional aguardando aprovacao — vira badge em
  // Prefeituras/Bancos. Fica no layout (e nao em cada tela) porque o menu
  // precisa da contagem mesmo com o operador em outra pagina.
  //
  // Polling: cadastro novo nasce no site, sem nenhuma acao do operador, entao
  // nao ha evento no cliente pra invalidar o cache. 60s + refetch ao voltar pra
  // aba cobre o caso sem pesar (a query e' duas COUNT(*)). Aprovar/recusar
  // invalidam a chave na hora — ver prefeituras.tsx / bancos.tsx.
  const pendentesQ = useQuery({
    queryKey: ["admin", "cadastros-pendentes"],
    queryFn: () => atlas.admin.cadastrosPendentes(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  // A CHAVE E' A DO ITEM DE MENU, e por isso ela mudou junto com o item: o
  // badge e' lido por `badges[item.key]`, e o item passou a ser
  // `organizacoes` na feature 009. Deixar `prefeituras` aqui apagaria a
  // contagem de pendentes do menu sem erro nenhum — some o numero, e o
  // operador so' descobre a fila quando abre a tela por outro motivo.
  // Pedidos de cancelamento do servidor esperando resposta (#276). Nascem no
  // app do servidor, sem acao do operador — mesmo motivo do polling acima.
  // Responder invalida a chave (prefixo `consignacao-solicitacoes`). So'
  // consulta quem pode abrir a fila: sem a permissao, a rota responde 403.
  const podeVerPedidos = podeAcessar(permissoes, "solicitacoes-cancelamento");
  const pedidosQ = useQuery({
    queryKey: ["admin", "consignacao-solicitacoes", "contagem"],
    queryFn: () => atlas.admin.consignacaoSolicitacoes.contagem(),
    enabled: permissoesLidas && podeVerPedidos,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const badges: Record<string, number | undefined> = {
    organizacoes: pendentesQ.data?.prefeituras,
    bancos: pendentesQ.data?.bancos,
    "solicitacoes-cancelamento": pedidosQ.data?.abertas,
  };

  // Filtra o menu conforme as permissoes — remove itens que o usuario nao pode ver.
  // Sub-itens herdam a permissao do parent (ex: `servidores-importar` cai no
  // grant de `servidores`), a menos que tenham chave propria em RESOURCE_GROUPS.
  const filteredNav = useMemo(() => {
    return NAV.map((item) => {
      const badge = badges[item.key];
      if (!("children" in item) || !item.children) {
        return podeAcessar(permissoes, item.key) ? { ...item, badge } : null;
      }
      const parentOk = podeAcessar(permissoes, item.key);
      const kids = item.children.filter((c) => parentOk || podeAcessar(permissoes, c.key));
      if (kids.length === 0) return null;
      return { ...item, children: kids, badge };
    }).filter((x): x is NonNullable<typeof x> => x !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissoes, badges.organizacoes, badges.bancos, badges["solicitacoes-cancelamento"]]);

  const meQ = useQuery({ queryKey: ["admin", "me"], queryFn: () => atlas.admin.me(), staleTime: 60_000 });

  return (
    <AppShellAdmin
      brand={<AtlasBrand sub="Averbadora" />}
      topbarSlot={
        <>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{location.pathname}</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {meQ.data ? (
              <span
                style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }}
                title={`${meQ.data.email} (${meQ.data.perfil})`}
              >
                {meQ.data.nome}
              </span>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setMode(resolved === "dark" ? "light" : "dark")}>
              {resolved === "dark" ? "Tema claro" : "Tema escuro"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await atlas.logout().catch(() => undefined);
                window.localStorage.removeItem("atlas:role");
                window.localStorage.removeItem("atlas:tokens");
                nav("/login");
              }}
            >
              Sair
            </Button>
          </div>
        </>
      }
      nav={filteredNav}
      activeKey={activeKey}
      onNavigate={(item) => {
        if (item.href) nav(item.href);
      }}
    >
      <Outlet />
    </AppShellAdmin>
  );
}
